/**
 * Device Fingerprinting Utility for Refresh Token Security
 *
 * This module generates a device fingerprint from stable browser characteristics
 * to bind refresh tokens to specific devices. The fingerprint includes:
 *
 * - Core Identity: Platform, language, browser family (must match exactly on backend)
 * - Contextual Data: Screen resolution, hardware specs, timezone (tolerance allowed)
 *
 * The backend uses tolerance levels to distinguish between legitimate changes
 * (browser updates, monitor switching, travel) and suspicious activity
 * (token theft, device switching).
 */

export interface DeviceFingerprintData {
  browserFamily: string
  platform: string | null
  hardwareConcurrency: number | null
  language: string | null
  screenResolution: string | null
  screenColorDepth: number | null
  timezoneOffset: string
  deviceMemory?: number
}

/**
 * Extracts the browser family (e.g., "Chrome", "Firefox") without version numbers
 * This ensures fingerprints remain valid after browser updates
 */
function getBrowserFamily(userAgent: string): string {
  // Check for major browsers in order of specificity
  // Note: Must check Edge before Chrome as Edge includes "Chrome" in UA
  if (userAgent.includes('Edg/') || userAgent.includes('Edge/')) {
    return 'Edge'
  }
  if (userAgent.includes('Chrome/')) {
    return 'Chrome'
  }
  if (userAgent.includes('Firefox/')) {
    return 'Firefox'
  }
  if (userAgent.includes('Safari/') && !userAgent.includes('Chrome/')) {
    return 'Safari'
  }
  if (userAgent.includes('Opera/') || userAgent.includes('OPR/')) {
    return 'Opera'
  }

  return 'Unknown'
}

/**
 * Gets the user's timezone as a string offset (e.g., "-05:00", "+01:00")
 * This allows for travel while detecting device switches
 */
function getTimezoneOffset(): string {
  // Convert getTimezoneOffset() to ISO 8601 format (e.g., "-08:00", "+05:30")
  // Note: getTimezoneOffset() returns positive for west of UTC, so we invert the sign
  const offset = new Date().getTimezoneOffset()
  const sign = offset <= 0 ? '+' : '-'
  const hours = Math.floor(Math.abs(offset) / 60)
    .toString()
    .padStart(2, '0')
  const minutes = (Math.abs(offset) % 60).toString().padStart(2, '0')

  return `${sign}${hours}:${minutes}`
}

/**
 * Generates a device fingerprint from browser characteristics
 *
 * @returns DeviceFingerprintData object with all components
 */
export function generateDeviceFingerprint(): DeviceFingerprintData {
  // Safely access navigator and screen with fallbacks
  const safeNavigator = typeof navigator !== 'undefined' ? navigator : null
  const safeScreen = typeof screen !== 'undefined' ? screen : null

  const userAgent = safeNavigator?.userAgent || 'Unknown'

  // Extract core identity components - use null for missing values
  const platform = safeNavigator?.platform ?? null
  const language = safeNavigator?.language ?? null
  const browserFamily = getBrowserFamily(userAgent)

  // Extract contextual components - use null for missing values
  const screenResolution = safeScreen ? `${safeScreen.width}x${safeScreen.height}` : null
  const screenColorDepth = safeScreen?.colorDepth ?? null
  const timezoneOffset = getTimezoneOffset()
  const hardwareConcurrency = safeNavigator?.hardwareConcurrency ?? null

  // Optional: Device memory (not available in all browsers)
  // @ts-expect-error - deviceMemory is not standard but available in Chrome
  const deviceMemory = safeNavigator?.deviceMemory as number | undefined

  const fingerprint: DeviceFingerprintData = {
    platform,
    language,
    screenResolution,
    screenColorDepth,
    timezoneOffset,
    hardwareConcurrency,
    browserFamily,
  }

  // Only include deviceMemory if available
  if (deviceMemory !== undefined) {
    fingerprint.deviceMemory = deviceMemory
  }

  return fingerprint
}

/**
 * Serializes the device fingerprint for transmission to the backend
 *
 * @param fingerprint - DeviceFingerprintData object to serialize
 * @returns Base64-encoded JSON string
 */
export function serializeDeviceFingerprint(fingerprint: DeviceFingerprintData): string {
  const json = JSON.stringify(fingerprint)

  // Use base64 encoding for clean transmission in HTTP headers
  return btoa(json)
}

/**
 * Generates and serializes a device fingerprint for immediate use
 * This is the primary function to use when sending requests to the auth service
 *
 * @returns Base64-encoded device fingerprint string
 */
export function getDeviceFingerprintHeader(): string {
  const fingerprint = generateDeviceFingerprint()
  return serializeDeviceFingerprint(fingerprint)
}
